
import type { PropType } from 'vue'
import type Vue from 'vue'
import type { ExplainMessages, RegularFilter, RegularFilterSelectedValues } from '../../models'
import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'
import FiltersAside from '../../components/filters/filters-aside.component.vue'
import { mapActions, mapGetters } from '../../composables'
import { resolveExplainEndpointId } from '../../composables/explain/use-explain-browse'
import { useContextQuery } from '../../composables/use-context-query'
import { useEndpoints } from '../../composables/use-endpoints'
import { useX } from '../../composables/x/use-x'
import { CONTEXT_CONSTS } from '../../store/context/context.consts'
import { EpbSelect } from '../shared/epb-select'

export default defineComponent({
  name: 'ExplainFiltersAside',
  components: {
    EpbSelect,
    FiltersAside,
  },
  props: {
    messages: {
      type: Object as PropType<ExplainMessages>,
      required: true,
    },
    filters: {
      type: Object as PropType<Record<string, RegularFilter>>,
      required: true,
    },
    startOpened: {
      type: Boolean,
      default: false,
    },
    endpointDisabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const {
      proxy: { $store },
    } = getCurrentInstance() as { proxy: Vue }

    const filtersAsideRef = ref<{ open: () => void }>()

    const { endpoints, defaultEndpointId } = useEndpoints()
    const x = useX()
    const { query, clearQuery } = useContextQuery()

    const { context } = mapGetters($store, CONTEXT_CONSTS.moduleName, {
      context: CONTEXT_CONSTS.getters.context,
    })

    const resolvedEndpointId = computed(() =>
      resolveExplainEndpointId(context.value.endpointId, defaultEndpointId.value),
    )

    const { setFiltersSelectedValues, setEndpointId } = mapActions(
      $store,
      CONTEXT_CONSTS.moduleName,
      {
        setFiltersSelectedValues: CONTEXT_CONSTS.actions.setFiltersSelectedValues,
        setEndpointId: CONTEXT_CONSTS.actions.setEndpointId,
      },
    )

    const pendingEndpointId = ref(resolvedEndpointId.value)
    const isEndpointIdUpdated = computed(
      () => !props.endpointDisabled && pendingEndpointId.value !== resolvedEndpointId.value,
    )

    watch(resolvedEndpointId, value => {
      if (!pendingEndpointId.value || pendingEndpointId.value === 'search') {
        pendingEndpointId.value = value
      }
    })

    const open = (): void => {
      filtersAsideRef.value?.open()
    }

    const onAccept = async (
      selectedValues: Record<string, RegularFilterSelectedValues>,
    ): Promise<void> => {
      const endpointChanged = pendingEndpointId.value !== resolvedEndpointId.value

      if (endpointChanged) {
        await clearQuery()
      }

      await setEndpointId(pendingEndpointId.value)
      await setFiltersSelectedValues(selectedValues)

      if (query.value && !endpointChanged) {
        x.setQueryFromUrl(query.value)
      }

      emit('contextChanged')
    }

    return {
      baseClass: 'epb-explain-filters-aside',
      endpoints,
      pendingEndpointId,
      isEndpointIdUpdated,
      filtersAside: filtersAsideRef,
      open,
      onAccept,
    }
  },
})
